import { AlertTriangle, Plus, Shield, Trash2, X } from 'lucide-react';
import { memo, useState } from 'react';
import { Button } from '@/components/ui';
import { useCaveats, useCreateCaveat, useDeleteCaveat, useUpdateCaveat } from '@/hooks/use-caveats';
import { cn } from '@/lib/cn';
import { handleMutationError } from '@/lib/error-handling';
import { toast } from '@/stores/toast-store';
import type { Caveat } from '@/types';

interface Props {
  loanId: string;
}

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

const STATUS_COLORS: Record<string, { badge: string; text: string }> = {
  ACTIVE: { badge: 'bg-green-100', text: 'text-green-700' },
  EXPIRED: { badge: 'bg-red-100', text: 'text-red-700' },
  WITHDRAWN: { badge: 'bg-gray-100', text: 'text-gray-700' },
  LAPSED: { badge: 'bg-amber-100', text: 'text-amber-700' },
};

export const CaveatsTab = memo(function CaveatsTab({ loanId }: Props) {
  const { data: caveats = [] } = useCaveats(loanId);
  const createCaveat = useCreateCaveat(loanId);
  const updateCaveat = useUpdateCaveat(loanId);
  const deleteCaveat = useDeleteCaveat(loanId);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [propertyDescription, setPropertyDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [lodgedDate, setLodgedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [state, setState] = useState('NSW');
  const [status, setStatus] = useState('ACTIVE');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim() || !propertyDescription.trim()) {
      toast.error('Company name and property description are required');
      return;
    }

    if (editingId) {
      updateCaveat.mutate(
        {
          id: editingId,
          companyName: companyName.trim(),
          propertyDescription: propertyDescription.trim(),
          amount: amount ? parseFloat(amount) : null,
          lodgedDate: lodgedDate || null,
          expiryDate: expiryDate || null,
          state: state || null,
          status,
          reason: reason.trim() || null,
          notes: notes.trim() || null,
        },
        {
          onSuccess: () => {
            toast.success('Caveat updated');
            resetForm();
          },
          onError: (err: any) => {
            handleMutationError(err, 'Failed to update caveat');
          },
        },
      );
    } else {
      createCaveat.mutate(
        {
          companyName: companyName.trim(),
          propertyDescription: propertyDescription.trim(),
          amount: amount ? parseFloat(amount) : undefined,
          lodgedDate: lodgedDate || undefined,
          expiryDate: expiryDate || undefined,
          state: state || undefined,
          status,
          reason: reason.trim() || undefined,
          notes: notes.trim() || undefined,
        },
        {
          onSuccess: () => {
            toast.success('Caveat created');
            resetForm();
          },
          onError: (err: any) => {
            handleMutationError(err, 'Failed to create caveat');
          },
        },
      );
    }
  };

  const resetForm = () => {
    setCompanyName('');
    setPropertyDescription('');
    setAmount('');
    setLodgedDate('');
    setExpiryDate('');
    setState('NSW');
    setStatus('ACTIVE');
    setReason('');
    setNotes('');
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (caveat: Caveat) => {
    setCompanyName(caveat.companyName);
    setPropertyDescription(caveat.propertyDescription);
    setAmount(caveat.amount || '');
    setLodgedDate(caveat.lodgedDate ? caveat.lodgedDate.split('T')[0] : '');
    setExpiryDate(caveat.expiryDate ? caveat.expiryDate.split('T')[0] : '');
    setState(caveat.state || 'NSW');
    setStatus(caveat.status);
    setReason(caveat.reason || '');
    setNotes(caveat.notes || '');
    setEditingId(caveat.id);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Delete this caveat? This cannot be undone.')) {
      deleteCaveat.mutate(id, {
        onSuccess: () => toast.success('Caveat deleted'),
        onError: (err: any) => handleMutationError(err, 'Failed to delete caveat'),
      });
    }
  };

  const isExpiringoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isOverdue = (expiryDate?: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const inputClass =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-900">Property Caveats</h3>
          {caveats.length > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold text-indigo-700 bg-indigo-100 rounded-full">
              {caveats.length}
            </span>
          )}
        </div>
        {!showForm && (
          <Button
            variant="primary"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 w-fit"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Caveat
          </Button>
        )}
      </div>

      {/* New/Edit Form */}
      {showForm && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-3">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">
              {editingId ? 'Edit Caveat' : 'New Caveat'}
            </h4>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  required
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="e.g. XYZ Finance Ltd"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                <select
                  value={state}
                  onChange={e => setState(e.target.value)}
                  className={inputClass}
                >
                  {STATES.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Property Description *
              </label>
              <textarea
                required
                value={propertyDescription}
                onChange={e => setPropertyDescription(e.target.value)}
                placeholder="e.g. 123 Main Street, Sydney NSW 2000"
                rows={2}
                className={cn(inputClass, 'resize-none')}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  className={inputClass}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="WITHDRAWN">Withdrawn</option>
                  <option value="LAPSED">Lapsed</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                <input
                  type="text"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Reason"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Lodged Date</label>
                <input
                  type="date"
                  value={lodgedDate}
                  onChange={e => setLodgedDate(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expiry Date</label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={e => setExpiryDate(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={2}
                className={cn(inputClass, 'resize-none')}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
              <Button
                variant="secondary"
                type="button"
                onClick={resetForm}
                className="w-fit px-3 py-1.5 text-xs font-medium"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={createCaveat.isPending || updateCaveat.isPending}
                className="px-3 py-1.5 text-xs font-medium text-white rounded-md hover: disabled:opacity-50"
              >
                {editingId ? 'Update Caveat' : 'Create Caveat'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Caveats List */}
      {caveats.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Shield className="w-8 h-8 mx-auto mb-2 opacity-25" />
          <p className="text-sm">No caveats recorded for this loan</p>
        </div>
      ) : (
        <div className="space-y-2">
          {caveats.map(caveat => {
            const colors = STATUS_COLORS[caveat.status] || {
              badge: 'bg-gray-100',
              text: 'text-gray-700',
            };
            const expiring = isExpiringoon(caveat.expiryDate);
            const overdue = isOverdue(caveat.expiryDate);

            return (
              <div
                key={caveat.id}
                className={cn(
                  'rounded-lg border p-3 space-y-2',
                  overdue
                    ? 'border-red-200 bg-red-50'
                    : expiring
                      ? 'border-amber-200 bg-amber-50'
                      : 'border-gray-200 bg-white',
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-medium text-gray-900">{caveat.companyName}</h4>
                      <span
                        className={cn(
                          'inline-block px-2 py-0.5 text-xs font-medium rounded',
                          colors.badge,
                          colors.text,
                        )}
                      >
                        {caveat.status}
                      </span>
                      {overdue && (
                        <span className="flex items-center gap-1 text-red-700 text-xs font-medium">
                          <AlertTriangle className="w-3 h-3" />
                          Expired
                        </span>
                      )}
                      {expiring && (
                        <span className="flex items-center gap-1 text-amber-700 text-xs font-medium">
                          <AlertTriangle className="w-3 h-3" />
                          Expiring soon
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mb-1">{caveat.propertyDescription}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="secondary"
                      onClick={() => handleEdit(caveat)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover: rounded transition-colors"
                    >
                      <span className="text-xs font-medium">Edit</span>
                    </Button>
                    <button
                      onClick={() => handleDelete(caveat.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 text-xs text-gray-600">
                  {caveat.amount && (
                    <div>
                      <span className="text-gray-500">Amount:</span>
                      <p className="font-medium text-gray-900">
                        ${parseFloat(caveat.amount).toFixed(2)}
                      </p>
                    </div>
                  )}
                  {caveat.lodgedDate && (
                    <div>
                      <span className="text-gray-500">Lodged:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(caveat.lodgedDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {caveat.expiryDate && (
                    <div>
                      <span className="text-gray-500">Expiry:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(caveat.expiryDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {caveat.state && (
                    <div>
                      <span className="text-gray-500">State:</span>
                      <p className="font-medium text-gray-900">{caveat.state}</p>
                    </div>
                  )}
                </div>

                {caveat.reason && (
                  <div className="text-xs">
                    <span className="text-gray-500">Reason:</span>
                    <p className="text-gray-700">{caveat.reason}</p>
                  </div>
                )}

                {caveat.notes && (
                  <div className="text-xs">
                    <span className="text-gray-500">Notes:</span>
                    <p className="text-gray-700">{caveat.notes}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
